import React, { useState } from 'react'
import { Formik, Form } from 'formik'
import * as Yup from 'yup'
import axios from 'axios'
import { Input } from 'components/ui/input'
import { Label } from 'components/ui/label'
import { Button } from 'components/ui/button'
import { Alert } from 'components/ui/alert'
import { Loader2, MailCheck } from 'lucide-react'

const validationSchema = Yup.object().shape({
	email: Yup.string().email('Invalid email address').required('Email is required'),
})

const ForgotPasswordCN = () => {
	const [errorMessage, setErrorMessage] = useState('')
	const [submitted, setSubmitted] = useState(false)

	if (submitted) {
		return (
			<div className="flex flex-col items-center text-center py-4">
				<div className="w-12 h-12 rounded-full bg-green-50 flex items-center justify-center mb-4">
					<MailCheck className="w-6 h-6 text-green-600" />
				</div>
				<h2 className="text-xl font-semibold text-[#074f9b] mb-2">Check Your Email</h2>
				<p className="text-sm text-gray-500">
					Instructions on how to reset your password have been sent to your email address.
				</p>
			</div>
		)
	}

	return (
		<Formik
			initialValues={{ email: '' }}
			validationSchema={validationSchema}
			onSubmit={async (values, { setSubmitting }) => {
				setErrorMessage('')
				try {
					const config = { headers: { 'Content-Type': 'application/json' } }
					const { data } = await axios.post('/api/changePasswords/request', { email: values.email }, config)
					if (data.success) {
						setSubmitted(true)
					} else {
						setErrorMessage('Unable to process your request. Please try again.')
					}
				} catch (error: any) {
					const msg =
						error.response?.data?.message ||
						(error.message === 'Network Error'
							? 'Unable to connect to the server. Please check your internet connection.'
							: 'Something went wrong. Please try again.')
					setErrorMessage(msg)
				} finally {
					setSubmitting(false)
				}
			}}
		>
			{({ isSubmitting, handleChange, handleBlur, values, errors, touched }) => (
				<Form className="space-y-5">
					<h2 className="text-2xl font-semibold text-center text-[#074f9b]">
						Forgot Password?
					</h2>
					<p className="text-sm text-gray-500 text-center">
						Enter your email and we'll send you instructions to reset your password.
					</p>

					{errorMessage && <Alert variant="danger">{errorMessage}</Alert>}

					<div className="space-y-1.5">
						<Label htmlFor="fp-email">Email</Label>
						<Input
							id="fp-email"
							name="email"
							type="email"
							placeholder="Enter your email"
							value={values.email}
							onChange={handleChange}
							onBlur={handleBlur}
							className="h-11"
						/>
						{touched.email && errors.email && (
							<p className="text-xs text-red-500 mt-1">{errors.email}</p>
						)}
					</div>

					<Button
						type="submit"
						isLarge
						disabled={isSubmitting || !values.email.trim()}
						className="w-full"
					>
						{isSubmitting ? (
							<>
								<Loader2 className="w-4 h-4 animate-spin" />
								Sending...
							</>
						) : (
							'Reset My Password'
						)}
					</Button>
				</Form>
			)}
		</Formik>
	)
}

export default ForgotPasswordCN
